import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import OriginStory from './components/OriginStory';
import MissionControl from './components/MissionControl';
import TeamUniverse from './components/TeamUniverse';
import CultureLab from './components/CultureLab';

const About = () => {
  useEffect(() => {
    // Smooth scroll behavior for anchor links
    const handleSmoothScroll = (e) => {
      const target = e?.target?.getAttribute('href');
      if (target && target?.startsWith('#')) {
        e?.preventDefault();
        const element = document.querySelector(target);
        if (element) {
          element?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    document.addEventListener('click', handleSmoothScroll);
    return () => document.removeEventListener('click', handleSmoothScroll);
  }, []);

  return (
    <>
      <Helmet>
        <title>About Us - Shutter Lifters | Where Code Meets Creativity</title>
        <meta 
          name="description" 
          content="Discover the story behind Shutter Lifters - from our humble beginnings to becoming a leading 3D interactive software development company. Meet our team and explore our culture." 
        />
        <meta name="keywords" content="about shutter lifters, software development team, 3D web development, company culture, tech team" />
        <meta property="og:title" content="About Us - Shutter Lifters" />
        <meta property="og:description" content="Meet the brilliant minds behind innovative 3D web experiences and transformative software solutions." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/about" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-24 pb-12 bg-gradient-to-br from-background via-background to-muted/20 relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl floating-animation"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-success/5 rounded-full blur-3xl floating-animation" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-8">
                <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-accent">About Shutter Lifters</span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold text-foreground mb-8 text-shadow-3d">
                Where Code Meets
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent to-success">
                  Creativity
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-muted-foreground max-w-4xl mx-auto mb-12 leading-relaxed">
                We are the architects of digital transformation, crafting immersive 3D experiences that bridge the gap between imagination and reality. Every project is a canvas where technical precision meets creative vision.
              </p>

              {/* Quick Navigation */}
              <div className="flex flex-wrap justify-center gap-4">
                {[
                  { label: "Our Story", href: "#origin-story", icon: "Clock" },
                  { label: "Mission & Values", href: "#mission-control", icon: "Target" },
                  { label: "Meet the Team", href: "#team-universe", icon: "Users" },
                  { label: "Our Culture", href: "#culture-lab", icon: "Beaker" }
                ]?.map((item, index) => (
                  <a
                    key={index}
                    href={item?.href}
                    className="inline-flex items-center space-x-2 bg-card/50 border border-border px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-card hover:border-accent/30 transition-all duration-300 backdrop-blur-sm"
                  >
                    <span>{item?.label}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Main Content Sections */}
        <OriginStory />
        <MissionControl />
        <TeamUniverse />
        <CultureLab />

        {/* Call to Action Section */}
        <section className="py-20 bg-gradient-to-r from-accent/10 via-background to-success/10 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-accent/5 to-success/5 opacity-50"></div>
          </div>

          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative">
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Ready to Transform Your Ideas?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let's collaborate and lift the shutter on your digital possibilities. Our team is ready to bring your vision to life with cutting-edge technology and creative excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center space-x-2 bg-accent text-accent-foreground px-8 py-4 rounded-lg font-semibold shadow-accent hover:bg-accent/90 transition-all duration-300 transform hover:scale-105"
              >
                <span>Start Your Project</span>
              </a>
              <a
                href="/portfolio"
                className="inline-flex items-center justify-center space-x-2 bg-transparent border-2 border-accent text-accent px-8 py-4 rounded-lg font-semibold hover:bg-accent/10 transition-all duration-300"
              >
                <span>View Our Work</span>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-card border-t border-border py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-accent to-accent/70 rounded-lg flex items-center justify-center">
                  {/* <span className="text-accent-foreground font-bold text-lg">SL</span> */}
                  <img src="/logo.jpeg" alt="" />
                </div>
                <span className="text-2xl font-bold text-foreground">Shutter Lifters</span>
              </div>
              <p className="text-muted-foreground mb-6">
                Lifting the shutter on digital possibilities since 2024
              </p>
              <div className="flex justify-center space-x-6">
                {[
                  { name: "LinkedIn", href: "#" },
                  { name: "GitHub", href: "#" },
                  { name: "Twitter", href: "#" },
                  { name: "Dribbble", href: "#" }
                ]?.map((social) => (
                  <a
                    key={social?.name}
                    href={social?.href}
                    className="text-muted-foreground hover:text-accent transition-colors duration-300"
                  >
                    {social?.name}
                  </a>
                ))}
              </div>
              <div className="mt-8 pt-8 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  © {new Date()?.getFullYear()} Shutter Lifters. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default About;